import { GetServerSidePropsContext } from "next";
import { useTranslation } from "next-i18next";
import React from "react";

import { userIsBusinessAccountHolder } from "@/api/profile";
import { Button } from "@/components/elements";
import { NotificationBanner } from "@/components/NotificationBanner";
import { ProductAccordion } from "@/components/ProductDetails/ProductAccordion";
import { ProductCarousel } from "@/components/ProductDetails/ProductCarousel";
import { ProductDetails } from "@/components/ProductDetails/ProductDetails";
import { SKU_CODES_FOR_BUSINESS_ACCOUNTS } from "@/constants";
import { useCart } from "@/hooks/useCart";
import useProductDetailsPage from "@/hooks/useProductDetailsPage";
import { ProductLayout } from "@/layouts/ProductLayout";
import { getSingleProductData } from "@/services/productService/productService";
import { ProductWithMetadata } from "@/types/api/products";
import { I18nextLocale } from "@/types/types";
import { isWebshopClosed, toTitleCase } from "@/utils/helpers";
import { getAllTranslationsServerSide } from "@/utils/i18n";
import { getLocaleParts } from "@/utils/localisation-utils.js";

import styles from "./styles.module.css";

export default function ProductPage({
  product,
  isProductAvailable,
}: {
  product: ProductWithMetadata;
  isProductAvailable: boolean;
}) {
  const { t } = useTranslation("product");

  const {
    id,
    brand,
    description,
    price,
    title,
    relativePackaging,
    size,
    images,
    ingredients,
    instructions,
    dispensers,
    size_original,
    skuCode,
    category,
    swan_license_number,
  } = product;

  const { handleDispenserReadMoreClick, getInStock } = useProductDetailsPage();
  const { addItem } = useCart();

  const inStock = getInStock(skuCode) && isProductAvailable;

  const addToCart = () => {
    addItem(
      {
        id,
        price: price.price,
        quantity: 1,
      },
      "products"
    );
  };

  const additionalInfo = {
    ingredients,
    instructions,
    dispensers,
    description,
    swan_license_number,
  };

  const productTitle = (): string => {
    const containsBrandName = title
      .toLowerCase()
      .startsWith(brand.name.toLowerCase());

    let customTitle = containsBrandName
      ? title
      : `${toTitleCase(brand.name)} ${title}`;

    customTitle = `${customTitle} | ${t(`categories.${category.key}`)} | På(fyll)™`;

    return customTitle;
  };

  const productInfo = {
    title: productTitle(),
    description,
  };

  const productDetails = {
    title,
    brand,
    price: price.formatted,
    pricePerLitre: price.litrePrice,
    relativePackaging,
    size_original,
    size,
    swan_license_number,
  };

  return (
    <ProductLayout productInfo={productInfo}>
      <ProductCarousel
        altText={title}
        images={images}
        className={styles.carousel}
      />
      <section className={styles.productDetails}>
        <ProductDetails productDetails={productDetails} />

        {isWebshopClosed ? null : (
          <Button
            onClick={addToCart}
            width="full"
            disabled={!inStock}
            className={styles.addToCart}
          >
            {t(inStock ? "add_CTA" : "out_of_stock")}
          </Button>
        )}

        <ProductAccordion
          productDetails={additionalInfo}
          onClick={handleDispenserReadMoreClick}
        />
      </section>

      <div className={styles.breakout}>
        <NotificationBanner breakout />
      </div>
    </ProductLayout>
  );
}

export async function getServerSideProps({
  locale,
  params,
  req,
}: GetServerSidePropsContext) {
  const slug = Array.isArray(params.slug) ? params.slug[0] : params.slug;
  let isProductAvailable = true;

  const { lang } = getLocaleParts(locale);

  const [product, isBusinessAccountHolder] = await Promise.all([
    getSingleProductData(slug, lang),
    userIsBusinessAccountHolder(req),
  ]);

  if (!product) {
    return {
      notFound: true,
    };
  }

  if (
    SKU_CODES_FOR_BUSINESS_ACCOUNTS.includes(product.skuCode) &&
    !isBusinessAccountHolder
  ) {
    isProductAvailable = false;
  }

  return {
    props: {
      ...(await getAllTranslationsServerSide(locale as I18nextLocale, [
        "product",
      ])),
      product,
      isProductAvailable: isProductAvailable,
    },
  };
}
